import React, { useState, useEffect, useMemo } from "react";
import { X, ChevronLeft, ChevronRight, PlaneTakeoff, Clock, Calendar as CalendarIcon, Navigation } from "lucide-react";
import AppConst from "../AppConst";
import { useAuth } from "../context/AuthContext";
import { BookingDto, AircraftRentalDetailsDto } from "../types";
import { BCM_STYLE as S } from "../styles/BookingCalendarStyle";
import { useAppContext } from "../context/AppContext";
import InstrumentLoader from "./InstrumentLoader";
import ConfirmationAlert from "./ConfirmationAlert";
import { message } from "antd";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  registration: string;
  hourlyRate: number;
  intervalStep: number;
  rentalDetails?: AircraftRentalDetailsDto;
}

const AircraftRentalAvailabilityModal = ({ isOpen, onClose, registration, hourlyRate, intervalStep, rentalDetails }: Props) => {
  const { requestAircraftRentalBooking } = useAppContext();
  const { currentUser } = useAuth();
  const [viewDate, setViewDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlots, setSelectedSlots] = useState<any[]>([]);
  const [missionPurpose, setMissionPurpose] = useState<string | null>(null);
  const [customPurpose, setCustomPurpose] = useState("");
  const [localLoading, setLocalLoading] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const missionOptions = [
      { label: "Ture de pistă", value: "Ture de pistă" },
      { label: "Raid aerian", value: "Raid" },
      { label: "Zbor de instrucție", value: "Instrucție" },
      { label: "Zbor verificare", value: "Tehnic" },
      { label: "Foto/Video aerian", value: "Foto/Video" },
      { label: "Altul", value: "Altul" },
    ];

  const rows = [
        { label: "Dimineață", start: 6, end: 10 },
        { label: "Prânz", start: 10, end: 14 },
        { label: "După-amiază", start: 14, end: 18 }
    ];

  const SIMPLE_CONFIRMATION_DICT = {
    title: "Validare // Programare Zbor",
    subtitle: "Confirmare Rezervare",
    description: (
      <>
        Ești pe cale să trimiți o cerere de rezervare pentru acest slot.
        Vei primi o notificare după ce deținătorul aeronavei validează zborul.
      </>
    ),
    info: [
      "Poți anula această rezervare din coș fără penalizări până la pragul de 72h doar după confirmarea de către proprietar.",
      "Plata va deveni disponibilă imediat după confirmarea administratorului."
    ],
    btnCancel: "Anulează",
    btnConfirm: "Trimite Cererea"
  };

  const CONFIRMATION_DICT = {
    title: "Validare // Programare Zbor",
    subtitle: "Confirmare Rezervare",
    description: (
      <>
         Această rezervare include sloturi în următoarele{" "}
        <span className="text-white font-bold underline decoration-red-500/50">
          72 de ore
        </span>.
      </>
    ),
    info: [
      <>
        Rezervarea <span className="text-red-400 font-bold">nu va mai putea fi anulată</span> odată trimisă către hangar.
      </>,
      <>
        Neplata după confirmarea proprietarului atrage <span className="text-red-400 font-bold">suspendarea permanentă (BAN)</span> a contului conform Termenilor și Condițiilor HangarX.
      </>,
      "Asigurați-vă că planul de zbor și documentele sunt conforme cu reglementările în vigoare."
    ],
    btnCancel: "Anulează",
    btnConfirm: "Trimite Cererea"
  };

  const [activeDict, setActiveDict] = useState(SIMPLE_CONFIRMATION_DICT);

  const content = AppConst.CONTENT;

  const timeSlotDtoList = rentalDetails?.calendarPayloadDto?.timeSlotDtoList || [];

  const allPossibleSlots = useMemo(() => {
    if (!selectedDate) return [];
    const baseDate = new Date(selectedDate);
    baseDate.setHours(0, 0, 0, 0);
    const slots = [];

    const startHour = 6;
    const endHour = 18;

    const totalMinutes = (endHour - startHour) * 60;

    for (let offset = 0; offset < totalMinutes; offset += intervalStep) {
      const currentTotalMinutes = startHour * 60 + offset;
      const h = Math.floor(currentTotalMinutes / 60);
      const m = currentTotalMinutes % 60;

      const sTime = new Date(baseDate).setHours(h, m, 0, 0);
      const eTime = sTime + intervalStep * 60000;

      const matchingSlot = timeSlotDtoList.find(slotDto => {
        if (!slotDto.from || !slotDto.to) return false;
        const fYear = slotDto.from.year ?? today.getFullYear();
        const fMonth = (slotDto.from.month ?? 1) - 1;
        const fDay = slotDto.from.day ?? 1;
        const fHour = slotDto.from.hour ?? 0;
        const fMinute = slotDto.from.minute ?? 0;

        const slotStartTime = new Date(fYear, fMonth, fDay, fHour, fMinute, 0, 0).getTime();

        const tYear = slotDto.to.year ?? today.getFullYear();
        const tMonth = (slotDto.to.month ?? 1) - 1;
        const tDay = slotDto.to.day ?? 1;
        const tHour = slotDto.to.hour ?? 0;
        const tMinute = slotDto.to.minute ?? 0;
        const slotEndTime = new Date(tYear, tMonth, tDay, tHour, tMinute, 0, 0).getTime();

        return sTime >= slotStartTime && sTime < slotEndTime;
      });

      const slotTypeEnumMap: Record<number, string> = {
        1: 'FREE_FOR_RENT',
        2: 'BLOCKED_BY_USER'
      };

      const mappedState = matchingSlot ? slotTypeEnumMap[matchingSlot.timeSlotType!] : 'default';

      let computedState = 'default';
      if (mappedState === 'FREE_FOR_RENT') {
        computedState = 'AVAILABLE';
      } else if (mappedState === 'BLOCKED_BY_USER') {
        computedState = 'MAINTENANCE';
      }

      slots.push({
        startTime: sTime,
        endTime: eTime,
        state: computedState,
        label: `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`,
        slotDto: matchingSlot
      });
    }
    return slots;
  }, [selectedDate, timeSlotDtoList, intervalStep]);

  useEffect(() => {
    if (isOpen && timeSlotDtoList.length > 0) {
      const acum = Date.now();

      const viitoareDisponibile = timeSlotDtoList.filter((slotDto) => {
        if (!slotDto.from || slotDto.timeSlotType !== 1) return false;
        const fYear = slotDto.from.year ?? today.getFullYear();
        const fMonth = (slotDto.from.month ?? 1) - 1;
        const fDay = slotDto.from.day ?? 1;
        const fHour = slotDto.from.hour ?? 0;
        const fMinute = slotDto.from.minute ?? 0;
        const slotStartTime = new Date(fYear, fMonth, fDay, fHour, fMinute, 0, 0).getTime();
        return slotStartTime >= acum;
      });

      if (viitoareDisponibile.length > 0) {
        viitoareDisponibile.sort((a, b) => {
          const aTime = new Date(a.from!.year!, (a.from!.month! - 1), a.from!.day!, a.from!.hour!, a.from!.minute!).getTime();
          const bTime = new Date(b.from!.year!, (b.from!.month! - 1), b.from!.day!, b.from!.hour!, b.from!.minute!).getTime();
          return aTime - bTime;
        });

        const f = viitoareDisponibile[0].from!;
        const celMaiApropiatSlot = new Date(f.year!, (f.month! - 1), f.day!, f.hour!, f.minute!);
        setViewDate(celMaiApropiatSlot);
      } else {
        setViewDate(new Date());
      }
    }
  }, [isOpen, timeSlotDtoList]);

  const availableDatesSet = useMemo(() => {
    const set = new Set<string>();
    if (!timeSlotDtoList) return set;

    timeSlotDtoList.forEach((slotDto) => {
      if (slotDto.timeSlotType === 1 && slotDto.from) {
        const fYear = slotDto.from.year ?? today.getFullYear();
        const fMonth = (slotDto.from.month ?? 1) - 1;
        const fDay = slotDto.from.day ?? 1;
        const dataCheie = new Date(fYear, fMonth, fDay).toDateString();
        set.add(dataCheie);
      }
    });
    return set;
  }, [timeSlotDtoList]);

  const toggleSlot = (slotObj: any) => {
    setSelectedSlots(prev =>
      prev.some(s => s.startTime === slotObj.startTime)
        ? prev.filter(s => s.startTime !== slotObj.startTime)
        : [...prev, slotObj].sort((a, b) => a.startTime - b.startTime)
    );
  };

  const firstSlot = selectedSlots.length > 0 ? selectedSlots[0].startTime : null;
  const duration = useMemo(() => {
    return selectedSlots.length * intervalStep;
  }, [selectedSlots, intervalStep]);

  const startTime = useMemo(() => {
      if (!firstSlot) return null;
      return new Date(firstSlot).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
    }, [firstSlot]);

  const handleRequest = async () => {
   if (!selectedDate || selectedSlots.length === 0 || !missionPurpose) return;

   const now = Date.now();
   const seventyTwoHoursInMs = 72 * 60 * 60 * 1000;

   const earliestSlot = Math.min(...selectedSlots.map(s => s.startTime));
   const isWithin72Hours = (earliestSlot - now) < seventyTwoHoursInMs;

   if (isWithin72Hours) {
        setActiveDict(CONFIRMATION_DICT);
      } else {
        setActiveDict(SIMPLE_CONFIRMATION_DICT);
      }
      setIsAlertOpen(true);
  };

  const executeBooking = async () => {
    if (selectedSlots.length === 0) return;
    setIsAlertOpen(false);
    setLocalLoading(true);
    try {
      const formattedTimeSlots = selectedSlots.map(slot => {
        if (slot.slotDto) {
          return slot.slotDto;
        }

        const startDate = new Date(slot.startTime);
        const endDate = new Date(slot.endTime);

        return {
          uuid: crypto.randomUUID ? crypto.randomUUID() : String(Math.random()),
          bookingId: 0,
          timeSlotType: 1,
          from: {
            timePointType: 1,
            year: startDate.getFullYear(),
            month: startDate.getMonth() + 1,
            day: startDate.getDate(),
            hour: startDate.getHours(),
            minute: startDate.getMinutes()
          },
          to: {
            timePointType: 2,
            year: endDate.getFullYear(),
            month: endDate.getMonth() + 1,
            day: endDate.getDate(),
            hour: endDate.getHours(),
            minute: endDate.getMinutes()
          }
        };
      }).filter(Boolean);

      if (formattedTimeSlots.length === 0) {
        message.error({ content: "Nu a fost găsit niciun slot valid selectat.", duration: AppConst.msgDuration });
        return;
      }

      const totalMilliseconds = selectedSlots.length * (intervalStep * 60000);
      const totalHours = totalMilliseconds / 3600000.0;
      const price = rentalDetails ? totalHours * (rentalDetails.grossValue || 0) : 0;

      const aircraftRentalBookingDto = {
        user: currentUser,
        aircraftRental: rentalDetails,
        timeSlotDtoList: formattedTimeSlots,
        missionPurpose: missionPurpose === "Altul" ? customPurpose : missionPurpose,
        estimatedPrice: price,
        status: "PENDING",
        paymentStatus: "UNPAID",
        createdAt: Date.now()
      };

      const success = await requestAircraftRentalBooking(aircraftRentalBookingDto);

      if (success) {
        message.success({ content: "Cererea ta a fost trimisă...", duration: AppConst.msgDuration });
        setSelectedSlots([]);
        setMissionPurpose(null);
        setCustomPurpose("");
        onClose();
      } else {
        message.error({ content: "Eroare la trimitere...", duration: AppConst.msgDuration });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLocalLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) { setSelectedDate(null); setSelectedSlots([]); setMissionPurpose(null); setCustomPurpose(""); }
  }, [isOpen]);

  if (!isOpen) return null;

  const currentMonth = viewDate.getMonth();
  const currentYear = viewDate.getFullYear();
  const monthNames = ["IAN", "FEB", "MAR", "APR", "MAI", "IUN", "IUL", "AUG", "SEP", "OCT", "NOI", "DEC"];
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const adjustedFirstDay = new Date(currentYear, currentMonth, 1).getDay() === 0 ? 6 : new Date(currentYear, currentMonth, 1).getDay() - 1;

  return (
      <div className={S.overlay}>
        <InstrumentLoader visible={localLoading} />
        <ConfirmationAlert
            isOpen={isAlertOpen}
            onClose={() => setIsAlertOpen(false)}
            onConfirm={executeBooking}
            dict={activeDict}
          />
        <div className={S.modal}>

          <div className={S.sidebarLeft}>
            <div className={S.stepHeader}>
              <div className={S.stepIconBlue}><CalendarIcon size={20} /></div>
              <div>
                <p className={S.stepLabel}>{content.step1}</p>
                <h3 className={S.stepTitle}>{content.step1Title}</h3>
              </div>
            </div>

            <div className={S.calendarNav}>
              <button
                onClick={() => setViewDate(new Date(currentYear, currentMonth - 1, 1))}
                className={S.calendarNavBtn}
                disabled={currentYear <= today.getFullYear() && currentMonth <= today.getMonth()}
              >
                <ChevronLeft size={18} style={{ opacity: (currentYear <= today.getFullYear() && currentMonth <= today.getMonth()) ? 0.3 : 1 }}/>
              </button>
              <span className={S.monthDisplay}>{monthNames[currentMonth]} {currentYear}</span>
              <button onClick={() => setViewDate(new Date(currentYear, currentMonth + 1, 1))} className={S.calendarNavBtn}><ChevronRight size={18}/></button>
            </div>

            <div className={S.calendarGrid}>
              {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map(h => <div key={h} className={S.weekdayLabel}>{h}</div>)}
              {Array.from({ length: adjustedFirstDay }).map((_, idx) => <div key={`spacer-${idx}`} />)}
              {Array.from({ length: daysInMonth }, (_, i) => {
                const d = new Date(currentYear, currentMonth, i + 1);
                const dStr = d.toDateString();
                const isSelected = selectedDate?.getDate() === (i + 1) && selectedDate?.getMonth() === currentMonth;
                const isPast = d < today;

                const hasNoSlots = !availableDatesSet.has(dStr);
                const isDayDisabled = isPast || hasNoSlots;

                return (
                  <button
                    key={i}
                    disabled={isDayDisabled}
                    onClick={() => {
                      if (!isDayDisabled) {
                        setSelectedDate(d);
                        setSelectedSlots([]);
                      }
                    }}
                    className={`
                      ${S.dayButtonBase}
                      ${isSelected ? S.dayButtonActive : S.dayButtonInactive}
                      ${isDayDisabled ? 'opacity-20 cursor-not-allowed grayscale bg-slate-950/10' : ''}
                    `}
                  >
                    {i + 1}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={S.mainContent}>
            <div className="space-y-10">
              <section className={!selectedDate ? S.sectionDisabled : S.sectionActive}>
                <div className={S.timelineSection}>
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <div className={S.clockGlow}></div>
                      <div className={S.clockIconBox}>
                        <Clock size={20} className="animate-spin-slow" style={{ animationDuration: '10s' }} />
                      </div>
                    </div>
                    <div>
                      <p className={S.stepLabel}>{content.step2}</p>
                      <h3 className={S.timelineHeaderTitle}>
                        {content.step2Title} <span className={S.utcTag}>{content.step2UTC}</span>
                      </h3>
                    </div>
                  </div>

                  <div className={S.timelineLegendBox}>
                    {S.legendItems.map((item) => (
                      <div key={item.label} className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${item.color}`}></div>
                        <span className={S.legendText}>{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-6">
                  {rows.map((row, idx) => (
                    <div key={idx} className="group/row relative">
                      <div className="flex items-center gap-2 mb-2 opacity-30">
                        <div className={S.rowDivider}></div>
                      </div>
                      <div className={S.slotsGrid}>
                        {allPossibleSlots.filter(s => {
                          const h = new Date(s.startTime).getHours();
                          return h >= row.start && h < row.end;
                        }).map((slot) => {
                          const isAvailable = slot.state === 'AVAILABLE';
                          const isMaintenance = slot.state === 'MAINTENANCE';
                          const isDefault = slot.state === 'default';
                          const isSelected = selectedSlots.some(s => s.startTime === slot.startTime);

                          let stateKey: keyof typeof S.slotStates = 'default';
                          if (isSelected) stateKey = 'selected';
                          else if (isMaintenance) stateKey = 'maintenance';
                          else if (isAvailable) stateKey = 'available';
                          else stateKey = 'default';

                          return (
                            <button
                              key={slot.startTime}
                              disabled={!isAvailable}
                              onClick={() => toggleSlot(slot)}
                              className={`${S.slotBase} ${S.slotStates[stateKey]}`}
                            >
                              <span className={`text-[10px] font-mono font-bold ${isSelected ? S.slotTextActive : S.slotTextInactive}`}>
                                {slot.label}
                              </span>
                              <span className={`${S.slotStatusLabel} ${isSelected ? S.slotStatusActive : S.slotStatusInactive}`}>
                                {isSelected ? 'ACTIVE' : isMaintenance ? 'BLOCKED' : isAvailable ? 'OPEN' : 'N/A'}
                              </span>
                              {isAvailable && !isSelected && <div className={S.slotDot}></div>}
                              {isSelected && (
                                <div className={S.slotPing}>
                                  <span className={S.pingContainer}>
                                    <span className={S.pingAnimate}></span>
                                    <span className={S.pingInner}></span>
                                  </span>
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className={`${S.missionSection} ${selectedSlots.length === 0 ? S.sectionDisabled : S.sectionActive}`}>
                            <div className="flex items-center gap-3 mb-6">
                              <div className={S.iconBoxPurple}><Navigation size={20} /></div>
                              <div>
                                <p className={S.stepLabel}>{content.step3}</p>
                                <h3 className={S.stepTitle}>{content.step3Title}</h3>
                              </div>
                            </div>

                            <div className={S.missionGrid}>
                              {missionOptions.map(opt => (
                                <button
                                  key={opt.value}
                                  onClick={() => setMissionPurpose(opt.value)}
                                  className={`${S.missionBtnBase} ${missionPurpose === opt.value ? S.missionBtnActive : S.missionBtnInactive}`}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>

                            {missionPurpose === "Altul" && (
                              <div className={S.customPurposeContainer}>
                                <input
                                  type="text"
                                  placeholder={content.purposePlaceholder}
                                  value={customPurpose}
                                  onChange={(e) => setCustomPurpose(e.target.value)}
                                  className={S.customPurposeInput}
                                />
                              </div>
                            )}
                          </section>
            </div>
          </div>

          <div className={S.sidebarRight}>
            <button onClick={onClose} className={S.closeBtn}><X size={20} /></button>

            <div className="flex-1 space-y-8">
              <div>
                 <p className={S.unitLabel}>{content.rentalUnit}</p>
                 <h4 className={S.unitReg}>{registration}</h4>
              </div>

              <div className={S.detailsCard}>
                <div className={`${S.detailRow} ${S.rowBorderB}`}>
                  <span className={S.detailLabel}>{content.date}</span>
                  <span className={S.detailValueDefault}>{selectedDate?.toLocaleDateString('ro-RO') || '---'}</span>
                </div>

                <div className={S.detailRow}>
                  <span className={S.detailLabel}>{content.startTime}</span>
                  <span className={S.detailValueTime}>{startTime || '--:--'}</span>
                </div>

                <div className={`${S.detailRow} ${S.rowBorderT}`}>
                  <span className={S.detailLabel}>{content.duration}</span>
                  <span className={S.detailValueDefault}>
                    {duration
                      ? `${Math.floor(duration / 60).toString().padStart(2, '0')}:${(duration % 60).toString().padStart(2, '0')}h`
                      : '---'}
                    </span>
                </div>

                <div className={`${S.detailRow} ${S.rowBorderT}`}>
                  <span className={S.detailLabel}>{content.purpose}</span>
                  <span className={S.detailValuePurpose}>
                    {missionPurpose === "Altul" ? customPurpose || '---' : missionPurpose || '---'}
                  </span>
                </div>
              </div>

              <div className={S.costCard}>
                 <p className={S.costLabel}>{content.price}</p>
                 <div className="flex items-baseline gap-1">
                   <span className={S.priceValue}>
                     €{(((duration || 0) / 60) * hourlyRate).toFixed(2)}
                   </span>
                   <span className={S.currencyLabel}>{content.currency}</span>
                 </div>
              </div>
            </div>

            <button
              type="button"
              onClick={handleRequest}
              disabled={!startTime || !missionPurpose || (missionPurpose === "Altul" && !customPurpose)}
              className={`${S.confirmBtnBase} ${startTime && missionPurpose ? S.confirmBtnActive : S.confirmBtnDisabled}`}
            >
              {localLoading ? 'SE PROCESEAZĂ...' : content.button} <PlaneTakeoff size={18} />
            </button>
          </div>
        </div>
      </div>
  );
};

export default AircraftRentalAvailabilityModal;